import type { EvalVerdict } from "@global/types/ai-evals";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Chip, Paper, Typography } from "@mui/material";
import React from "react";
import { pct, type Row, VERDICTS } from "./scoring";

const VERDICT_CHIP: Record<EvalVerdict, { color: "success" | "warning" | "error" | "default"; icon: React.ReactElement }> = {
  pass: { color: "success", icon: <CheckCircleIcon /> },
  concerns: { color: "warning", icon: <WarningAmberIcon /> },
  fail: { color: "error", icon: <CancelIcon /> },
  na: { color: "default", icon: <RemoveCircleOutlineIcon /> },
};

/** Icon plus label, so a verdict never rests on color alone. */
export const VerdictChip: React.FC<{ verdict: EvalVerdict }> = ({ verdict }) => (
  <Chip size="small" variant="outlined" label={verdict} color={VERDICT_CHIP[verdict].color} icon={VERDICT_CHIP[verdict].icon} />
);

export const verdictColor: Record<EvalVerdict, string> = {
  pass: "success.main",
  concerns: "warning.main",
  fail: "error.main",
  na: "grey.300",
};

/** Points against the baseline; nothing when either side is missing. */
export const Delta: React.FC<{ now: number | null; before: number | null | undefined }> = ({ now, before }) => {
  if (now === null || before === null || before === undefined) return null;
  const d = Math.round((now - before) * 100);
  const color = d > 0 ? "success.dark" : d < 0 ? "error.main" : "text.secondary";
  return (
    <Typography component="span" variant="body2" sx={{ color, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {d === 0 ? "±0" : d > 0 ? `▲ +${d}` : `▼ ${d}`}
    </Typography>
  );
};

/** Score as a bar; the dark tick marks the baseline. */
export const ScoreBar: React.FC<{ now: number | null; before?: number | null }> = ({ now, before }) => (
  <Box role="img" aria-label={`${pct(now)} of 100`} sx={{ position: "relative", height: 10, minWidth: 140, bgcolor: "grey.200", borderRadius: "0 4px 4px 0" }}>
    <Box sx={{ position: "absolute", inset: 0, width: `${(now ?? 0) * 100}%`, bgcolor: "primary.main", borderRadius: "0 4px 4px 0" }} />
    {before !== null && before !== undefined && (
      <Box sx={{ position: "absolute", top: -3, bottom: -3, width: 2, left: `calc(${before * 100}% - 1px)`, bgcolor: "text.primary", borderRadius: 1 }} />
    )}
  </Box>
);

/** How the verdicts of a check split up, one segment per verdict with a 2px gap. */
export const VerdictMix: React.FC<{ rows: Row[] }> = ({ rows }) => (
  <Box aria-hidden sx={{ display: "flex", gap: "2px", height: 10, minWidth: 100 }}>
    {VERDICTS.map((v) => {
      const count = rows.filter((r) => r.verdict === v).length;
      return count ? <Box key={v} title={`${v}: ${count}`} sx={{ flex: count, bgcolor: verdictColor[v], borderRadius: "2px" }} /> : null;
    })}
  </Box>
);

const VERDICT_LABEL: Record<EvalVerdict, string> = { pass: "pass", concerns: "concerns", fail: "fail", na: "n/a" };

/** "41 pass · 9 concerns · 3 fail · 12 n/a" with a colored dot each, so the score's makeup is readable without the colors. */
export const VerdictCounts: React.FC<{ rows: Row[] }> = ({ rows }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.25, rowGap: 0.25, mt: 0.5 }}>
    {VERDICTS.map((v) => (
      <Typography key={v} component="span" variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", bgcolor: verdictColor[v], mr: 0.5 }} />
        {rows.filter((r) => r.verdict === v).length} {VERDICT_LABEL[v]}
      </Typography>
    ))}
  </Box>
);

/** Sequential blue, light = low; the text flips to white on the dark steps. */
const HEAT = ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95", "#0d366b"];
export function heat(value: number | null): { bgcolor?: string; color?: string } {
  if (value === null) return { color: "text.disabled" };
  const step = Math.min(HEAT.length, Math.max(1, Math.ceil(value * HEAT.length)));
  return { bgcolor: HEAT[step - 1], color: step >= 5 ? "#fff" : "#0b0b0b" };
}
export const HeatLegend: React.FC = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
      Score 0
    </Typography>
    {HEAT.map((c) => (
      <Box key={c} sx={{ width: 14, height: 10, bgcolor: c, borderRadius: "2px" }} />
    ))}
    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
      100
    </Typography>
  </Box>
);

export const StatCard: React.FC<{ label: string; value: React.ReactNode; detail?: React.ReactNode }> = ({ label, value, detail }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: "1 1 160px" }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
    {detail}
  </Paper>
);

export const Pre: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="pre"
    sx={{ m: 0, p: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace", fontSize: 12, bgcolor: "grey.50", border: 1, borderColor: "divider", borderRadius: 1 }}
  >
    {children}
  </Box>
);
